import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { ListingData } from '../types';
import {
  formatAddressLineOne,
  formatAddressLineTwo,
} from '../../../system/location';

interface SkeletonCardProps {
  listing?: ListingData;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ listing }) => {
  return (
    <Card
      sx={{
        height: '100%',
        width: '280px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.secondary',
        border: 1,
        borderRadius: '12px',
        borderColor: 'divider',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      {/* Image container with status badge overlay */}
      <Box sx={{ position: 'relative', mt: 3 }}>
        <Skeleton variant="rectangular" height={200} animation="wave" />
      </Box>

      <CardContent
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" width={80} height={24} animation="wave" />
        </Box>

        <Box sx={{ mb: 1 }}>
          {listing ? (
            <>
              <Typography variant="h6" color="primary.main" gutterBottom>
                {formatAddressLineOne({
                  streetName: listing.Property.STREET_NAME,
                  streetDirPrefix: listing.Property.STREET_DIR_PREFIX,
                  streetNumber: listing.Property.STREET_NUMBER,
                  streetSuffix: listing.Property.STREET_SUFFIX,
                  streetDirSuffix: listing.Property.STREET_DIR_SUFFIX,
                  unitNumber: listing.Property.UNIT_NUMBER,
                })}
              </Typography>
              {listing.Property.CITY && (
                <Typography variant="h6" color="primary.main" gutterBottom>
                  {formatAddressLineTwo({
                    city: listing.Property.CITY,
                    state: listing.Property.STATE_OR_PROVINCE,
                    zipCode: listing.Property.POSTAL_CODE,
                    zipCodePlus4: listing.Property.POSTAL_CODE_PLUS4,
                  })}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Skeleton
                variant="text"
                width="80%"
                height={32}
                animation="wave"
                sx={{ mb: 1 }}
              />
              <Skeleton
                variant="text"
                width="60%"
                height={32}
                animation="wave"
                sx={{ mb: 1 }}
              />
            </>
          )}

          <Skeleton
            variant="rounded"
            width="100%"
            height={32}
            animation="wave"
            sx={{ mb: 1 }}
          />

          {listing?.Property.LISTING_ID ? (
            <Typography
              color="text.primary"
              display="block"
              sx={{ fontSize: '14px' }}
            >
              MLS#: {listing.Property.LISTING_ID}
            </Typography>
          ) : (
            <Skeleton variant="text" width={100} height={20} animation="wave" />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={32}
            animation="wave"
          />
        </Box>
      </CardContent>
    </Card>
  );
};
